import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Star, ChatCircle } from "@phosphor-icons/react";

function Card({ label, value, sub, tone = "default" }) {
  const tones = {
    default: "text-slate-900",
    good: "text-emerald-700",
    warn: "text-amber-700",
    danger: "text-red-700",
    accent: "text-[#EA580C]",
  };
  return (
    <div className="bg-white border border-slate-200 p-4 rounded-sm">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className={`text-[28px] font-bold mt-2 font-mono-num ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function StarRow({ n }) {
  return (
    <span className="text-amber-500 tracking-tighter">
      {"★".repeat(n || 0)}
      <span className="text-slate-300">{"★".repeat(5 - (n || 0))}</span>
    </span>
  );
}

export default function Csat() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const r = await api.get("/service/csat-summary");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <>
        <PageHeader title="Client Feedback (CSAT)" subtitle="Loading…" module="Service · SV" />
        <div className="p-8 text-slate-500">Loading…</div>
      </>
    );
  }

  const npsTone = data.nps_score >= 50 ? "good" : data.nps_score >= 0 ? "warn" : "danger";
  const ratingTone = data.avg_rating >= 4 ? "good" : data.avg_rating >= 3 ? "warn" : "danger";

  return (
    <div data-testid="csat-page">
      <PageHeader
        title="Client Feedback · CSAT"
        subtitle="Customer Satisfaction (CSAT) and Net Promoter Score (NPS) — sourced from resolved service tickets"
        module="Service · SV"
      />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="csat-summary-cards">
          <Card label="Responses" value={data.count} sub="Total submitted" />
          <Card label="Avg Rating" value={data.avg_rating || "—"} sub="Out of 5" tone={ratingTone} />
          <Card label="NPS Score" value={data.nps_score || 0} sub="Promoters − Detractors" tone={npsTone} />
          <Card label="Promoters" value={data.promoters} sub="Rated 9-10" tone="good" />
          <Card label="Detractors" value={data.detractors} sub="Rated 0-6" tone="danger" />
        </div>

        <div>
          <div className="text-[11.5px] font-semibold uppercase tracking-[0.15em] text-slate-700 mb-2 flex items-center gap-1.5">
            <ChatCircle size={13} /> Latest feedback
          </div>
          {data.recent.length === 0 ? (
            <EmptyState title="No feedback yet" description="Resolve a service ticket and share the auto-generated feedback link with the client." />
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm" data-testid="csat-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="label-caps">Ticket</TableHead>
                    <TableHead className="label-caps">Client</TableHead>
                    <TableHead className="label-caps">Rating</TableHead>
                    <TableHead className="label-caps">NPS</TableHead>
                    <TableHead className="label-caps">Comment</TableHead>
                    <TableHead className="label-caps">Submitted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.recent.map((r) => (
                    <TableRow key={r.ticket_no} data-testid={`csat-row-${r.ticket_no}`}>
                      <TableCell className="font-mono-num font-semibold text-[#EA580C]">{r.ticket_no}</TableCell>
                      <TableCell className="text-slate-700">{r.client_name || "—"}</TableCell>
                      <TableCell><StarRow n={r.feedback_rating} /></TableCell>
                      <TableCell className="font-mono-num font-semibold">{r.feedback_nps}</TableCell>
                      <TableCell className="text-slate-600 text-[12px] max-w-md truncate">{r.feedback_comment || "—"}</TableCell>
                      <TableCell className="text-slate-500 font-mono-num text-xs">{fmtDate(r.feedback_submitted_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
